/**
 * Service: Avatar Generation
 * Wraps avatar generation edge functions and storage operations.
 * These are critical workflow actions exposed via Edge Functions.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SELFIE HERE IS NOT AN IDENTITY ARTEFACT. DO NOT REUSE IT FOR KYC.
 *
 * The image this service uploads is captured for ONE purpose — generating the
 * card avatar — under the avatar-generation consent, and it is DELETED by
 * `cleanup-selfies` once that purpose is spent (72h guest / tenant retention on
 * terminal requests). It is never retained.
 *
 * A future identity or liveness capture is a DIFFERENT artefact: different
 * purpose, separately-recorded consent, different (RETAINED) retention rule,
 * and its own storage path. Reaching for this path instead — "we already have a
 * photo of their face" — is the efficiency that looks obvious and is a purpose
 * -limitation breach plus a retention contradiction (identity needs the image
 * kept; this pipeline is built to destroy it, and the reaper will).
 *
 * If identity capture is built: add a new capture step, a new consent, a new
 * bucket path and a new retention rule. Do not widen this one.
 * See docs/SECURITY-INVARIANTS.md → "Selfie purpose limitation".
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { supabase } from "@/integrations/supabase/client";
import { makeAvatarIdempotencyKey } from "@/lib/idempotency";

export interface AvatarGeneration {
  id: string;
  variant_number: number;
  generated_image_url: string | null;
  status: string;
  generation_batch_id?: string | null;
  selfie_storage_path?: string;
  user_id?: string;
  error_message?: string | null;
}

export interface GenerationResult {
  generationIds: string[];
  status: "completed" | "all_failed";
  charged: "bank" | "free" | "credit" | null;
  refunded: boolean;
}

/* ── Edge Function: get signed upload URL (path is server-issued) ── */

export async function getUploadUrl(ext?: string) {
  return supabase.functions.invoke("generate-avatar", {
    body: { action: "get-upload-url", ext },
  });
}

/* ── Direct upload to storage via signed URL ── */

export async function uploadSelfie(signedUrl: string, file: File) {
  const res = await fetch(signedUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Selfie upload failed: ${errText}`);
  }
  return res;
}

/* ── B4: bind biometric consent to a specific selfie ──
 * Guest path: attach_selfie_to_guest_session (called via guestToken elsewhere).
 * Authenticated path: attach_selfie_to_user — MUST be called between uploadSelfie
 * and startGeneration or the server-side gate will refuse.
 */
export async function attachSelfieToUser(selfieStoragePath: string, policyVersion = 1) {
  return supabase.rpc("attach_selfie_to_user", {
    p_selfie_path: selfieStoragePath,
    p_policy_version: policyVersion,
  });
}

/* ── Edge Function: start avatar generation ── */

export async function startGeneration(payload: {
  /** B2B (/t/), admin and marketing-reel callers. Routes on tenant as before. */
  tenantId?: string;
  /** O5: Cardmoji's program-resolved prepaid surface. Server derives the platform tenant. */
  programId?: string;
  styleId: string;
  subStyleId?: string | null;
  themeId?: string | null;
  selfieStoragePath: string;
  numVariants?: number;
  guestToken?: string;
  /**
   * Batch scope for the idempotency key (order epoch, or a regenerate nonce).
   * NOT part of the request body — it only ever varies the key so a NEW batch of
   * work is actually run instead of replaying a completed one.
   */
  batchKey?: string;
}) {
  const { batchKey, ...body } = payload;

  const idempotencyKey = makeAvatarIdempotencyKey({
    ...body,
    batchKey,
    tenantId: payload.tenantId ?? `program:${payload.programId ?? "none"}`,
  });

  return supabase.functions.invoke("generate-avatar", {
    body,
    headers: { "Idempotency-Key": idempotencyKey },
  });
}



/* ── Edge Function: poll generation status ── */

export async function pollGenerations(generationIds: string[]) {
  return supabase.functions.invoke("generate-avatar?action=poll", {
    body: { generationIds },
  });
}

/* ── Edge Function: delete selfie ── */

export async function deleteSelfie(cardRequestId: string) {
  return supabase.functions.invoke("delete-selfie", {
    body: { cardRequestId },
  });
}

/* ── Reads (avatar generations table) ── */

export async function getGeneration(id: string) {
  return supabase
    .from("avatar_generations")
    .select("id, variant_number, generated_image_url, status, user_id, selfie_storage_path")
    .eq("id", id)
    .single();
}

/** @deprecated Alias for getGeneration */
export const getAvatarGeneration = getGeneration;

/**
 * Resolve many `selected_avatar_image_id`s to their image URLs in one read.
 * The account card list needs this: a card row stores the CHOSEN generation id,
 * not the URL, so without it an ordered card renders as a bare colour block.
 * Owner-scoped by RLS.
 */
export async function getGenerationUrls(ids: string[]) {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (unique.length === 0) return {} as Record<string, string>;
  const { data } = await supabase
    .from("avatar_generations")
    .select("id, generated_image_url")
    .in("id", unique);
  const map: Record<string, string> = {};
  for (const row of (data as { id: string; generated_image_url: string | null }[] | null) ?? []) {
    if (row.generated_image_url) map[row.id] = row.generated_image_url;
  }
  return map;
}


export async function listGenerationsForUser(userId: string, tenantId?: string) {
  let query = supabase
    .from("avatar_generations")
    .select("id, variant_number, generated_image_url, status, selfie_storage_path, created_at")
    .eq("user_id", userId)
    .eq("status", "completed");
  if (tenantId) query = query.eq("tenant_id", tenantId);
  // Chronological: the consumer's FULL accumulated set (all generations, all
  // variants) in the order they were produced, so S5 can offer every avatar.
  return query.order("created_at", { ascending: true }).order("variant_number", { ascending: true });
}

/** Default recency window for the S5 picker: this card attempt, in practice. */
export const GENERATION_WINDOW_HOURS = 24;

/**
 * The S5 picker's set: this user's completed generations for the CURRENT
 * taxonomy pick, inside a recency window. Scoping by style (and sub-choice /
 * hobby when the pick has them) is what stops the strip from showing every
 * avatar the account ever made across every style, and it naturally caps the
 * count at the consumer allowance (5 requests x numVariants).
 * RLS still scopes the read to the owner — these filters are UX, not security.
 */
export async function listGenerationsForPick(
  userId: string,
  pick: { styleId: string; subStyleId?: string | null; themeId?: string | null },
  windowHours: number = GENERATION_WINDOW_HOURS,
) {
  const since = new Date(Date.now() - windowHours * 3600 * 1000).toISOString();
  let query = supabase
    .from("avatar_generations")
    .select("id, variant_number, generated_image_url, status, selfie_storage_path, created_at")
    .eq("user_id", userId)
    .eq("status", "completed")
    .eq("style_id", pick.styleId)
    .gte("created_at", since);
  if (pick.subStyleId) query = query.eq("sub_choice_id", pick.subStyleId);
  if (pick.themeId) query = query.eq("hobby_id", pick.themeId);
  return query.order("created_at", { ascending: true }).order("variant_number", { ascending: true });
}

/**
 * Returns the newest still-running batch for this exact design request.
 * The database is authoritative; session storage is only used after the rows
 * have been found, so returning in a fresh tab can still rejoin safely.
 */
export async function listInFlightGenerationsForPick(
  userId: string,
  selfieStoragePath: string,
  pick: { styleId: string; subStyleId?: string | null; themeId?: string | null },
) {
  const since = new Date(Date.now() - 10 * 60 * 1000).toISOString();
  let query = supabase
    .from("avatar_generations")
    .select("id, variant_number, generated_image_url, status, generation_batch_id, created_at")
    .eq("user_id", userId)
    .eq("selfie_storage_path", selfieStoragePath)
    .eq("style_id", pick.styleId)
    .not("generation_batch_id", "is", null)
    .in("status", ["generating", "completed"])
    .gte("created_at", since)
    .order("created_at", { ascending: false })
    .limit(20);
  if (pick.subStyleId) query = query.eq("sub_choice_id", pick.subStyleId);
  else query = query.is("sub_choice_id", null);
  if (pick.themeId) query = query.eq("hobby_id", pick.themeId);
  else query = query.is("hobby_id", null);

  const result = await query;
  const newestBatchId = result.data?.[0]?.generation_batch_id;
  return {
    ...result,
    data: newestBatchId
      ? result.data?.filter((row) => row.generation_batch_id === newestBatchId) ?? []
      : [],
  };
}

/**
 * Attempts USED by this user, mirroring generate-avatar's server-side count
 * exactly: non-internal rows, scoped to the tenant, counted per REQUEST
 * (`variant_number = 1`, since each request writes variants 1..n).
 * Display only — the server remains the authority that enforces the limit.
 */
export async function countGenerationAttempts(userId: string, tenantId?: string) {
  let query = supabase
    .from("avatar_generations")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .eq("internal", false)
    .eq("counts_toward_allowance", true)
    .eq("variant_number", 1);
  if (tenantId) query = query.eq("tenant_id", tenantId);
  const { count, error } = await query;
  if (error) return 0;
  return count ?? 0;
}

/**
 * Is the caller staff (super_admin / admin) and therefore exempt from the
 * CONSUMER freeAttempts allowance?
 *
 * Display + client-gating only. The authoritative exemption is re-derived
 * server-side in generate-avatar from user_roles for auth.uid(); a consumer
 * spoofing this read gains nothing — the server still meters them.
 */
export async function isStaffGenerationExempt(userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .in("role", ["super_admin", "admin"])
    .limit(1);
  if (error) return false;
  return !!data?.length;
}





/**
 * RETIRED — do not reintroduce.
 *
 * avatar_generations.card_request_id is legacy history only: a third of its
 * populated rows point at an order other than the one that chose them, and
 * nothing has written it for months. Resolve a person's or an order's images
 * from card_requests.selected_avatar_image_id instead (see AdminReview and
 * handoff_resolve_avatar server-side).
 */


/** @deprecated Alias for listGenerationsForUser */
export const listCompletedGenerations = listGenerationsForUser;

export async function getMonthlyGenerations(monthStart: string) {
  return supabase
    .from("avatar_generations")
    .select("tenant_id")
    .gte("created_at", monthStart);
}

/* ── Edge Function: send avatar via email ── */

export async function sendAvatarEmail(payload: {
  recipientEmail: string;
  avatarUrl: string;
  cardholderName: string;
  tenantId: string;
}) {
  return supabase.functions.invoke("send-avatar-email", {
    body: payload,
  });
}

/* ── Storage: selfie signed URL ── */

export async function getSelfieSignedUrl(path: string, expiresIn = 3600) {
  return supabase.storage.from("selfies").createSignedUrl(path, expiresIn);
}

/* ── Edge Function: remove background from avatar ── */

export async function removeBackground(imageUrl: string, generationId?: string) {
  return supabase.functions.invoke("remove-background", {
    body: { imageUrl, generationId },
  });
}

/**
 * Cutout with fallback-to-original (same contract the B2B wizard uses at
 * wizard-context.tsx). The base prompt intentionally produces a flat
 * background; the cutout is this downstream step. Never throws — a failed
 * removal degrades to the raw image rather than blocking the reveal.
 */
export async function cutoutOrOriginal(generationId: string, imageUrl: string): Promise<string> {
  try {
    const { data } = await removeBackground(imageUrl, generationId);
    return (data as any)?.cutoutUrl || imageUrl;
  } catch (err) {
    console.warn("Background removal failed for", generationId, err);
    return imageUrl;
  }
}
